/**
 * Limiting reactant by the "extent of reaction" method.
 *
 * Divide the moles of each reactant by its coefficient. The reactant with the smallest
 * quotient runs out first, so it is the limiting reactant, and that quotient is how many
 * "reaction units" the equation can run:
 *
 *     ξ = min over reactants of  n(reactant) ÷ coefficient
 *
 * Products follow from ξ × coefficient, and each other reactant is left with
 * n − ξ × coefficient. A reactant given no amount is treated as in unlimited supply
 * (oxygen from the air, say) and takes no part in the comparison. Two reactants that tie are
 * both reported as limiting: the mixture is in exact stoichiometric proportion.
 */

import type { Species } from "@/lib/tool-kit/chem-reactions/balance";
import {
  isGas,
  speciesMass,
} from "@/lib/tool-kit/chem-reactions/stoichiometry";

export interface ReactantRow {
  index: number;
  species: Species;
  coefficient: number;
  /** Moles supplied; null when the supply is unlimited. */
  available: number | null;
  /** available ÷ coefficient; null when unlimited. */
  perCoefficient: number | null;
  /** coefficient × ξ. */
  used: number;
  /** available − used; null when unlimited. */
  leftover: number | null;
  leftoverGrams: number | null;
  molarMass: number | null;
  limiting: boolean;
}

export interface ProductRow {
  index: number;
  species: Species;
  coefficient: number;
  moles: number;
  grams: number | null;
  litres: number | null;
  molarMass: number | null;
}

export interface LimitingResult {
  /** ξ: how many times the equation runs, in mol of reaction. */
  extent: number;
  reactants: ReactantRow[];
  products: ProductRow[];
  /** More than one reactant ties for limiting. */
  tie: boolean;
  approximate: string[];
}

const TIE_TOLERANCE = 1e-9;

interface Given {
  index: number;
  moles: number;
  quotient: number;
}

function givenReactants(
  species: readonly Species[],
  coefficients: readonly number[],
  amounts: readonly (number | null)[]
): Given[] {
  const out: Given[] = [];
  for (const [index, item] of species.entries()) {
    const moles = amounts[index] ?? null;
    if (item.side === "reactant" && moles !== null) {
      out.push({ index, moles, quotient: moles / coefficients[index] });
    }
  }
  return out;
}

function reactantRow(
  species: Species,
  index: number,
  coefficient: number,
  available: number | null,
  extent: number,
  limiting: boolean
): ReactantRow {
  const { total } = speciesMass(species);
  const leftover =
    available === null ? null : Math.max(available - coefficient * extent, 0);
  return {
    index,
    species,
    coefficient,
    available,
    perCoefficient: available === null ? null : available / coefficient,
    used: coefficient * extent,
    leftover,
    leftoverGrams:
      leftover === null || total === null ? null : leftover * total,
    molarMass: total,
    limiting,
  };
}

function productRow(
  species: Species,
  index: number,
  coefficient: number,
  extent: number,
  litresPerMole: number | null
): ProductRow {
  const { total } = speciesMass(species);
  const moles = coefficient * extent;
  return {
    index,
    species,
    coefficient,
    moles,
    grams: total === null ? null : moles * total,
    litres:
      isGas(species) && litresPerMole !== null ? moles * litresPerMole : null,
    molarMass: total,
  };
}

/**
 * @param amounts moles per species index (reactants only matter); null for an unlimited
 *   supply.
 */
export function limitingReactant(
  species: readonly Species[],
  coefficients: readonly number[],
  amounts: readonly (number | null)[],
  litresPerMole: number | null
): LimitingResult | null {
  const given = givenReactants(species, coefficients, amounts);
  if (given.length === 0) {
    return null;
  }
  const extent = Math.min(...given.map((entry) => entry.quotient));
  const limitingIndexes = new Set(
    given
      .filter((entry) => entry.quotient <= extent * (1 + TIE_TOLERANCE))
      .map((entry) => entry.index)
  );
  const reactants: ReactantRow[] = [];
  const products: ProductRow[] = [];
  for (const [index, item] of species.entries()) {
    if (item.side === "reactant") {
      reactants.push(
        reactantRow(
          item,
          index,
          coefficients[index],
          amounts[index] ?? null,
          extent,
          limitingIndexes.has(index)
        )
      );
    } else {
      products.push(
        productRow(item, index, coefficients[index], extent, litresPerMole)
      );
    }
  }
  return {
    extent,
    reactants,
    products,
    tie: limitingIndexes.size > 1,
    approximate: species
      .filter((item) => speciesMass(item).approximate)
      .map((item) => item.label),
  };
}
